// #ux-redesign-start: passkeyService (Auth redesign · WebAuthn · Patients)
//
// Cliente del flow WebAuthn contra MS-Auth (PatientsPasskeyController). El
// ceremonial WebAuthn vive en el browser (navigator.credentials.{create,get})
// y este service sólo intermedia los 2 round-trips begin/finish con el back.
//
// Reusa `AuthService` (singleton) cuyo baseUrl ya es `<api>/auth/patients`
// (lo setea usersSaga.js con AuthService.setBaseUrl("/patients")) y maneja
// setToken/getToken para sesiones.
// ─────────────────────────────────────────────────────────────────────────────

import AuthService from 'plugins/TS-lib-utils-public/services/AuthService';

// ── helpers base64url <-> ArrayBuffer ───────────────────────────────────────
export const bufToB64u = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
};

export const b64uToBuf = (b64u) => {
  const b64 = (b64u + '==='.slice((b64u.length + 3) % 4))
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out.buffer;
};

export const bufToB64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = '';
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};

// Estandariza la forma en que extraemos status + data + error de los
// resultados de AxiosService (que swallowea errores y devuelve un objeto
// con `response`).
const unwrap = (result) => {
  if (!result) return { ok: false, status: 0, data: 'Network error' };

  // Éxito: respuesta normal de axios.
  if (result.status >= 200 && result.status < 300) {
    return { ok: true, data: result.data };
  }

  // AxiosService.post devuelve `error.request || error.response`, y ante un
  // error HTTP axios puebla LOS DOS, así que lo que llega es el
  // XMLHttpRequest — no la respuesta de axios. El XHR expone el status en
  // `.status` y el body crudo en `.response`/`.responseText`, no en
  // `.response.status` / `.response.data`. Leerlo como si fuera axios daba
  // status `undefined`, y con eso ningún chequeo de 404 podía funcionar.
  if (typeof result.status === 'number' && result.status > 0) {
    let body = result.data;
    if (body === undefined) {
      body = result.response !== undefined ? result.response : result.responseText;
    }
    if (typeof body === 'string') {
      try {
        body = JSON.parse(body);
      } catch (e) {
        // El backend manda texto plano en los errores: se usa tal cual.
      }
    }
    return { ok: false, status: result.status, data: body };
  }

  // Forma anidada de axios (error.response), por si el shape cambia.
  if (result.response && typeof result.response.status === 'number') {
    return {
      ok: false,
      status: result.response.status,
      data: result.response.data,
    };
  }

  return { ok: false, status: 0, data: 'Network error' };
};

// ── Email hint ─────────────────────────────────────────────────────────────
// Guardamos el email del último user que enroló una passkey en este browser,
// sólo para prellenar la UI. NUNCA es identidad: el `finish` del backend valida
// la firma criptográfica contra el credential real. Vive acá (y no en el
// componente) para que el saga de logout pueda limpiarlo sin importar React.
export const EMAIL_HINT_KEY = 'tsp-passkey-email-hint';

export const readPasskeyHint = () => {
  try {
    return (localStorage.getItem(EMAIL_HINT_KEY) || '').trim().toLowerCase();
  } catch (e) {
    return '';
  }
};

export const savePasskeyHint = (email) => {
  try {
    if (email) localStorage.setItem(EMAIL_HINT_KEY, email);
  } catch (e) {
    // localStorage bloqueado (modo privado): el hint no persiste, el flujo sigue.
  }
};

export const clearPasskeyHint = () => {
  try {
    localStorage.removeItem(EMAIL_HINT_KEY);
  } catch (e) {
    // idem: nada que limpiar si el storage no está disponible.
  }
};

// rpId derivado del browser. WebAuthn requiere hostname (no IP, no port).
const currentRpId = () => window.location.hostname;

// ── BEGIN / FINISH endpoints ───────────────────────────────────────────────
export const passkeyService = {
  registerBegin: async ({ email }) => {
    const result = await AuthService.post('/passkey/register/begin', {
      email,
      rpId: currentRpId(),
    });
    return unwrap(result);
  },

  registerFinish: async (body) => {
    const result = await AuthService.post('/passkey/register/finish', body);
    if (result && result.status === 200 && result.data && result.data.token) {
      // #ux-redesign: registerFinish ahora emite sesión (enrolar + entrar en un
      // paso). Persistimos el token igual que authFinish.
      AuthService.setToken(result.data.token);
    }
    return unwrap(result);
  },

  authBegin: async ({ email }) => {
    const result = await AuthService.post('/passkey/auth/begin', {
      email,
      rpId: currentRpId(),
    });
    return unwrap(result);
  },

  authFinish: async (body) => {
    const result = await AuthService.post('/passkey/auth/finish', body);
    if (result && result.status === 200 && result.data && result.data.token) {
      // Mismo patrón que AuthService.login: persistir el token para que las
      // siguientes requests autenticadas lo manden en el Authorization.
      AuthService.setToken(result.data.token);
    }
    return unwrap(result);
  },

  // Gestión de passkeys del user logueado (Account > Security).
  //
  // Estos dos endpoints van detrás de sessionMiddleware, a diferencia de los de
  // begin/finish. El token NO se adjunta por interceptor: cada método de
  // AuthService lo pasa a mano (ver getSessionList/deleteSession), así que
  // llamar a get/delete sin headers salía sin Authorization, el backend
  // respondía 401 y el interceptor de useSessionTimer deslogueaba al usuario
  // apenas abría la pantalla.
  list: async () => {
    const token = AuthService.getToken();
    if (!token) return { ok: false, status: 401, data: 'No session token' };
    const result = await AuthService.get('/passkey', {
      headers: { Authorization: `Bearer ${token}` },
    });
    return unwrap(result);
  },

  remove: async (credentialId) => {
    const token = AuthService.getToken();
    if (!token) return { ok: false, status: 401, data: 'No session token' };
    const result = await AuthService.delete(
      `/passkey/${encodeURIComponent(credentialId)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return unwrap(result);
  },
};

export default passkeyService;
// #ux-redesign-end
